/**
 * Standalone embed entry point for the chat widget.
 *
 * Usage (any HTML page):
 *
 *   <script src="https://cdn.emaanswers.com/chat/v1/chat-widget.umd.js" defer></script>
 *   <script>
 *     window.addEventListener('DOMContentLoaded', function () {
 *       PweChat.init({
 *         apiUrl: 'https://api.puglieseweb.com/chatbot',
 *         tenantId: 'your-tenant-id',
 *       });
 *     });
 *   </script>
 *
 * Everything the operator configures in the Ema Answers console (name,
 * greeting, chips, colour, avatar, corner, lead capture, privacy link) is
 * fetched from the API at mount, so the snippet needs only the two keys
 * above. The other options (README "Options") still work as fallbacks.
 *
 * `PweChat.open()`, `close()` and `toggle()` drive the panel from the host
 * page's own script (commands.ts); one sent before the panel has mounted is
 * kept.
 *
 * `init` is safe to call before <body> exists (it waits for DOMContentLoaded)
 * and safe to call twice (the second call is ignored). Including the script
 * twice — tag managers do — keeps the first copy.
 */

import { createElement } from 'react';
import { createRoot, type Root } from 'react-dom/client';
import { ChatWidget } from './ChatWidget';
import { validateConfig } from './config';
import { sendCommand } from './commands';
import { version } from './version';
import type { PweChatConfig } from './types';

export type { PweChatConfig } from './types';

export interface PweChatApi {
  /** The bundle's package version, e.g. '1.6.0'. Also the double-inclusion marker. */
  readonly version: string;
  init(config: PweChatConfig): void;
  destroy(): void;
  /** Open the chat panel. Safe straight after `init()`: it is kept until the panel mounts. */
  open(): void;
  /** Close the chat panel. */
  close(): void;
  /** Open the panel when it is closed, close it when it is open. */
  toggle(): void;
}

declare global {
  interface Window {
    PweChat?: PweChatApi;
  }
}

const ROOT_ID = 'pweb-chat-root';

function createApi(): PweChatApi {
  let root: Root | null = null;

  const api: PweChatApi = {
    version,

    open: () => sendCommand('open'),
    close: () => sendCommand('close'),
    toggle: () => sendCommand('toggle'),

    init(config: PweChatConfig) {
      if (typeof document === 'undefined') return;
      // Pasted into <head> (Squarespace "Header code injection" is a common
      // target) the snippet runs before <body> exists and `appendChild` used
      // to throw. Wait for the document instead.
      if (!document.body) {
        document.addEventListener('DOMContentLoaded', () => api.init(config), { once: true });
        return;
      }
      if (root || document.getElementById(ROOT_ID)) {
        console.warn('[PweChat] init() called more than once — the widget is already on this page. Ignoring.');
        return;
      }

      const safeConfig = validateConfig(config);
      if (!safeConfig) return;

      const container = document.createElement('div');
      container.id = ROOT_ID;
      container.style.position = 'fixed';
      container.style.zIndex = '99999';
      container.style.bottom = '0';
      container.style.right = '0';
      container.style.pointerEvents = 'none';
      document.body.appendChild(container);

      // Allow pointer events on children (the widget itself)
      const style = document.createElement('style');
      style.textContent = `#${ROOT_ID} > * { pointer-events: auto; }`;
      document.head.appendChild(style);

      root = createRoot(container);
      root.render(createElement(ChatWidget, safeConfig));
    },

    destroy() {
      if (root) {
        root.unmount();
        root = null;
      }
      const el = typeof document !== 'undefined' ? document.getElementById(ROOT_ID) : null;
      if (el) el.remove();
    },
  };

  return api;
}

// Double inclusion. The UMD wrapper assigns `window.PweChat = factory()` AFTER
// this module body runs, so a guard that merely skipped the assignment below
// would still be overwritten by the wrapper — the default export itself has to
// be the copy already on the page.
const existing = typeof window !== 'undefined' ? window.PweChat : undefined;
const alreadyLoaded = !!existing && typeof existing.version === 'string' && typeof existing.init === 'function';
if (alreadyLoaded) {
  console.warn(
    `[PweChat] chat-widget v${existing!.version} is already loaded on this page; the second copy (v${version}) was ignored. Include the script once.`,
  );
}

const PweChat: PweChatApi = alreadyLoaded ? existing! : createApi();

if (!alreadyLoaded && typeof window !== 'undefined') {
  window.PweChat = PweChat;
}

// The ONLY runtime export. With a named export beside it Rollup would make the
// UMD global `{ default: PweChat, ... }` and `window.PweChat.init` would be
// gone; vite.config.umd.ts pins `exports: 'default'` so that build fails
// instead of shipping. The version is `PweChat.version`; the React component
// is the package's main entry (src/index.ts).
export default PweChat;
export type { ChatWidgetProps } from './types';
